/**
 * The non-component half of `./ResearchLiveProvider`: the stream guard, the
 * context, and the reader hosts use. A module that exports a component may
 * export nothing else, or Vite's Fast Refresh rejects it.
 */
import * as React from "react";
import { STREAM_RESEARCH, isKnownStream } from "@authe/streaming";
import type { ResearchRequest } from "@/api/researchSource";
import { ResearchProgressTracker, researchUpdateFromRequest, type ResearchUpdate } from "./researchProgress";

/**
 * Whether this build's streaming registry knows the research stream
 * (`STREAM_RESEARCH`: tenancy + user audience, `{org}-{tenant}-research` and
 * `{org}-{tenant}-{user}-research`) — a guard
 * for a registry rolled back without this module: an unknown stream would get
 * every audience channel and be refused by the subscribe resolver, so nothing
 * subscribes and every host polls instead.
 *
 * A function, not a module-level constant: no call into another package at
 * import time (see the import-cycle note in the dashboard's memory).
 */
export function researchStreamKnown(): boolean {
  return isKnownStream(STREAM_RESEARCH);
}

/** How long a tracked request may go unheard before the background reads it, without the stream. */
export const BACKGROUND_POLL_MS = 5_000;
/** The same, while pushes arrive — a safety net for a dropped message, not the protocol. */
export const BACKGROUND_SAFETY_MS = 60_000;
/** Requests read per background tick. */
export const BACKGROUND_BATCH = 5;

/** Who heard an update: a push, or the background poll of a tracked request. */
export type ResearchUpdateSource = "stream" | "poll";

/**
 * A listener for updates heard outside any host — the stream, or the
 * background poll. A polled update also carries the full request.
 */
export type ResearchUpdateListener = (
  update: ResearchUpdate,
  source: ResearchUpdateSource,
  request: ResearchRequest | null,
) => void;

export interface ResearchLive {
  /** One per signed-in identity: decides once which subjects just finished. */
  tracker: ResearchProgressTracker;
  /** Pushes are arriving for this caller — the stream is known, entitled and open. */
  live: boolean;
  /** Hear every update the provider hears; returns the unsubscribe. */
  subscribe(listener: ResearchUpdateListener): () => void;
  /**
   * A request this tab just started: tracked, so its completion refreshes the
   * addresses wherever the reader has gone since — not only on the page that
   * asked — and told to every host listening, so one listing its context shows
   * it at once.
   */
  track(request: ResearchRequest): void;
}

export const ResearchLiveContext = React.createContext<ResearchLive | null>(null);

/**
 * What a host outside the provider gets (a test, a page rendered without the
 * signed-in shell): its own tracker, no stream, nothing heard from elsewhere.
 * Hosts still poll and still refresh what they observe finishing.
 */
export function standaloneResearchLive(): ResearchLive {
  const tracker = new ResearchProgressTracker();
  return {
    tracker,
    live: false,
    subscribe: () => () => undefined,
    track: (request) => tracker.track(researchUpdateFromRequest(request)),
  };
}

/** The provider's live state, or a standalone one. Stable per mount. */
export function useResearchLive(): ResearchLive {
  const shared = React.useContext(ResearchLiveContext);
  const [standalone] = React.useState(standaloneResearchLive);
  return shared ?? standalone;
}
